const TOKEN_KEY = 'membership_token';
const USER_KEY = 'membership_user';
const LEGACY_USER_INFO_KEY = 'userInfo';

const storage = () => window.sessionStorage;

/** 允许从 opener 同步 session 的路径前缀（须同源） */
const TRUSTED_OPENER_PATH_PREFIXES = ['/admin', '/play/video'];

function isTrustedOpener(opener) {
    if (!opener || opener.closed) {
        return false;
    }
    if (opener.origin !== window.location.origin) {
        return false;
    }
    const path = opener.location?.pathname || '';
    return TRUSTED_OPENER_PATH_PREFIXES.some(
        (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    );
}

/** JWT 中的 roleKey 与 session 用户信息不一致时，以 JWT 为准并回写 session */
function reconcileUserRoleFromJwt(user, token) {
    if (!user?.loginId || !token) {
        return user;
    }
    const jwtRole = getJwtRoleKeyFromToken(token);
    if (!jwtRole || user.roleKey === jwtRole) {
        return user;
    }
    const synced = { ...user, roleKey: jwtRole };
    storage().setItem(USER_KEY, JSON.stringify(synced));
    return synced;
}

function getJwtRoleKeyFromToken(token) {
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        return payload?.roleKey ?? null;
    } catch {
        return null;
    }
}

/** 解析 JWT payload 中的 exp（秒） */
function getTokenExpMs(token) {
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        if (payload.exp == null) return null;
        return payload.exp * 1000;
    } catch {
        return null;
    }
}

export function isTokenExpired(token) {
    if (!token) return true;
    const expMs = getTokenExpMs(token);
    if (expMs == null) return false;
    return Date.now() >= expMs;
}

export const getToken = () => storage().getItem(TOKEN_KEY);

/** 解析 JWT payload（不校验签名，仅用于与 session 用户信息对照） */
export function getJwtPayload() {
    const token = getToken();
    if (!token) return null;
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
}

export function getJwtRoleKey() {
    const token = getToken();
    if (!token) return null;
    return getJwtRoleKeyFromToken(token);
}

export const getUser = () => {
    const raw = storage().getItem(USER_KEY);
    if (!raw) {
        return null;
    }
    try {
        return JSON.parse(raw);
    } catch {
        storage().removeItem(USER_KEY);
        return null;
    }
};

export const setAuth = (token, user) => {
    storage().setItem(TOKEN_KEY, token);
    storage().setItem(USER_KEY, JSON.stringify(user));
    // 清除旧版 localStorage，避免关闭浏览器后仍能通过持久化 token 进入
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LEGACY_USER_INFO_KEY);
};

export const clearAuth = () => {
    storage().removeItem(TOKEN_KEY);
    storage().removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LEGACY_USER_INFO_KEY);
};

/**
 * 是否已登录：须存在有效 session 内 token、未过期、且有用户信息。
 */
export const isAuthenticated = () => {
    const token = getToken();
    const user = getUser();
    let reason = 'ok';

    if (!token) {
        reason = 'no_token';
    } else if (isTokenExpired(token)) {
        reason = 'expired';
        clearAuth();
    } else if (!user?.loginId) {
        reason = 'no_user';
    } else {
        reconcileUserRoleFromJwt(user, token);
    }

    return reason === 'ok';
};

/** 兼容旧版 localStorage；迁移到 sessionStorage 后删除 localStorage */
export const migrateLegacyAuth = () => {
    if (getToken()) {
        return;
    }
    const legacyToken = localStorage.getItem(TOKEN_KEY);
    const legacyUser = localStorage.getItem(USER_KEY);
    if (legacyToken && legacyUser) {
        try {
            const user = JSON.parse(legacyUser);
            if (user?.loginId && !isTokenExpired(legacyToken)) {
                setAuth(legacyToken, user);
            } else {
                localStorage.removeItem(TOKEN_KEY);
                localStorage.removeItem(USER_KEY);
            }
            return;
        } catch {
            localStorage.removeItem(USER_KEY);
        }
    }
    const legacy = localStorage.getItem(LEGACY_USER_INFO_KEY);
    if (!legacy) {
        return;
    }
    try {
        const parsed = JSON.parse(legacy);
        if (parsed.token && parsed.loginId && !isTokenExpired(parsed.token)) {
            setAuth(parsed.token, { loginId: parsed.loginId });
        }
    } catch {
        /* ignore */
    } finally {
        localStorage.removeItem(LEGACY_USER_INFO_KEY);
    }
};

/** 页面重新可见时复核登录态（供 App 调用） */
export const syncAuthState = () => isAuthenticated();

/**
 * 新标签页播放页从 opener 同步 session（新标签不共享 sessionStorage）。
 * @returns {boolean} 是否同步成功
 */
export const bootstrapAuthFromOpener = () => {
    if (isAuthenticated() || !window.opener) {
        return false;
    }
    try {
        if (!isTrustedOpener(window.opener)) {
            return false;
        }
        const token = window.opener.sessionStorage.getItem(TOKEN_KEY);
        const userRaw = window.opener.sessionStorage.getItem(USER_KEY);
        if (!token || !userRaw || isTokenExpired(token)) {
            return false;
        }
        const user = JSON.parse(userRaw);
        if (!user?.loginId) {
            return false;
        }
        setAuth(token, user);
        return true;
    } catch {
        return false;
    }
};
